import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import { CALENDAR_VIEW_TYPES } from 'constants/calendar';

import { calculateJumpInDateRange } from 'utils/calendar';
import { createDate } from 'utils/date';
import useAppContext from 'utils/hooks/useAppContext';

import { FILTER_TYPES } from 'operabase-router/constants';

// NOTE: Module-level state for sharing calendar range between components
let globalCalendarState = null;
let stateSubscribers = [];
let isInitialized = false;

// NOTE: Subscribe to state changes
const subscribe = callback => {
  stateSubscribers.push(callback);

  return () => {
    stateSubscribers = stateSubscribers.filter(sub => sub !== callback);
  };
};

// NOTE: Update global state and notify subscribers
const updateGlobalState = newState => {
  globalCalendarState = { ...globalCalendarState, ...newState };
  stateSubscribers.forEach(callback => callback(globalCalendarState));
};

// NOTE: Initialize state only once globally
const initializeGlobalState = initialStartDate => {
  if (!isInitialized) {
    globalCalendarState = {
      rangeStartDate: initialStartDate,
    };
    isInitialized = true;
  }
};

const useSharedCalendarRange = (minDate, maxDate) => {
  const { obRouteContext } = useAppContext();
  const { filterParams = {}, linkProps } = obRouteContext;
  const { filters } = linkProps || {};
  const { date_from, date_to } = filterParams;
  const { [FILTER_TYPES.JUMP_IN_EXPRESSION]: jumpInExp } = filters || {};

  const jumpInHighlightDates = useMemo(
    () => (Array.isArray(jumpInExp) ? jumpInExp.map(({ entity: { date } }) => date) : []),
    [jumpInExp],
  );

  const jumpInDates = useMemo(() => {
    if (jumpInExp?.length > 0) {
      const { jumpInStartDate, jumpInEndDate } = calculateJumpInDateRange(jumpInHighlightDates);
      return { jumpInStartDate, jumpInEndDate };
    }

    return { jumpInStartDate: null, jumpInEndDate: null };
  }, [jumpInExp, jumpInHighlightDates]);

  const calculateViewRangeStart = useCallback(
    ({ dateFrom }) => {
      if (dateFrom) {
        return createDate(dateFrom);
      }

      if (jumpInExp?.length > 0) {
        return createDate(jumpInDates?.jumpInStartDate);
      }

      return createDate();
    },
    [jumpInExp, jumpInDates],
  );

  // NOTE: Initialize global state only once, not on every component mount
  const [localState, setLocalState] = useState(() => {
    const initialStartDate = calculateViewRangeStart({ dateFrom: date_from });
    initializeGlobalState(initialStartDate);
    return globalCalendarState;
  });

  // NOTE: Subscribe to global state changes
  useEffect(() => {
    const unsubscribe = subscribe(newState => {
      setLocalState(newState);
    });
    return unsubscribe;
  }, []);

  // NOTE: Only update state when date_from actually changes from URL params (not component mounting)
  const prevDateFromRef = useRef(date_from);
  useEffect(() => {
    if (prevDateFromRef.current !== date_from) {
      const newStartDate = calculateViewRangeStart({ dateFrom: date_from });
      updateGlobalState({ rangeStartDate: newStartDate });
      prevDateFromRef.current = date_from;
    }
  }, [date_from, calculateViewRangeStart]);

  const { viewType, dateShiftValue, dateShiftUnit } = useMemo(() => {
    let startDate;
    let endDate;

    if (jumpInExp?.length > 0 && jumpInDates?.jumpInStartDate && jumpInDates?.jumpInEndDate) {
      startDate = jumpInDates.jumpInStartDate;
      endDate = jumpInDates.jumpInEndDate;
    } else {
      startDate = date_from;
      endDate = date_to ? createDate(date_to) : createDate(date_from).add(1, 'year');
    }

    const daysDiff = createDate(endDate).diff(createDate(startDate), 'day');

    if (daysDiff <= 15) {
      return {
        viewType: CALENDAR_VIEW_TYPES.FIFTEEN_DAYS,
        dateShiftValue: 15,
        dateShiftUnit: 'day',
      };
    }

    if (daysDiff <= 30) {
      return {
        viewType: CALENDAR_VIEW_TYPES.MONTH,
        dateShiftValue: 30,
        dateShiftUnit: 'day',
      };
    }

    if (daysDiff <= 90) {
      return {
        viewType: CALENDAR_VIEW_TYPES.QUARTER,
        dateShiftValue: 90,
        dateShiftUnit: 'day',
      };
    }

    return {
      viewType: CALENDAR_VIEW_TYPES.YEAR,
      dateShiftValue: 1,
      dateShiftUnit: 'year',
    };
  }, [date_from, date_to, jumpInExp, jumpInDates]);

  const onShiftViewRange = useCallback(
    direction => {
      const currentState = globalCalendarState;
      const currentStartDate = currentState.rangeStartDate;

      let newStartDate;

      if (direction === 'left') {
        newStartDate = currentStartDate.subtract(dateShiftValue, dateShiftUnit);
      } else {
        newStartDate = currentStartDate.add(dateShiftValue, dateShiftUnit);
      }

      updateGlobalState({ rangeStartDate: newStartDate });
    },
    [dateShiftValue, dateShiftUnit],
  );

  const rangeStartDate = localState?.rangeStartDate || createDate();

  const { rangeEndDate, allowRightShift, allowLeftShift } = useMemo(() => {
    const endDate = createDate(rangeStartDate)
      .add(dateShiftValue, dateShiftUnit)
      .subtract(1, 'day');

    return {
      rangeEndDate: jumpInExp ? jumpInDates?.jumpInEndDate : endDate,
      allowRightShift: maxDate ? endDate.isBefore(createDate(maxDate)) : true,
      allowLeftShift: minDate ? rangeStartDate.isAfter(createDate(minDate)) : true,
    };
  }, [rangeStartDate, dateShiftValue, dateShiftUnit, maxDate, minDate, jumpInExp, jumpInDates]);

  return {
    viewType,
    rangeStartDate,
    rangeEndDate,
    onShiftLeft: () => onShiftViewRange('left'),
    onShiftRight: () => onShiftViewRange('right'),
    allowRightShift,
    allowLeftShift,
    jumpInHighlightDates,
  };
};

export default useSharedCalendarRange;
